import { useContext, useCallback } from 'react';
import { CheckInContext } from '../context/CheckInContext';
import { CheckInContextType, Student } from '../context/types';
import { getStudentClasses, submitCheckIn } from '../services/api';

// Custom hook for accessing CheckIn context
export function useCheckIn(): CheckInContextType {
  const context = useContext(CheckInContext);
  if (context === undefined) {
    throw new Error('useCheckIn must be used within a CheckInProvider');
  }
  return context;
}

// Custom hook with business logic
export function useCheckInLogic() {
  const context = useCheckIn();
  const {
    state,
    setSelectedStudent,
    setAvailableClasses,
    setLoading,
    setError,
    resetForm,
    showThankYouMessage,
    hideThankYouMessage,
  } = context;

  // Fetch classes for a specific student
  const fetchStudentClasses = useCallback(async (studentId: string) => {
    try {
      const classes = await getStudentClasses(studentId);
      setAvailableClasses(classes);
      setError('');
    } catch (err) {
      setError('Failed to load classes for this student.');
      console.error('Error fetching student classes:', err);
    }
  }, [setAvailableClasses, setError]);

  // Handle student selection
  const handleStudentSelect = useCallback(async (student: Student) => {
    setSelectedStudent(student);
    await fetchStudentClasses(student.id);
  }, [setSelectedStudent, fetchStudentClasses]);

  // Handle check-in submission
  const handleCheckIn = useCallback(async () => {
    if (!state.selectedStudent || state.selectedClasses.length === 0) {
      return;
    }

    try {
      setLoading(true);
      await submitCheckIn(
        state.selectedStudent.id,
        state.selectedClasses
      );

      // Show thank you message
      const selectedClassNames = state.selectedClasses.map(id => state.availableClasses.find(ac => ac.id === id)?.name)
      showThankYouMessage(`You're all set! Checked in for ${selectedClassNames.join(', ')}`);

      // Reset form immediately
      resetForm();

      // Hide thank you message after 7 seconds
      setTimeout(() => {
        hideThankYouMessage();
      }, 4000);

      setError('');
    } catch (err) {
      setError('Check-in failed. Please try again.');
      console.error('Error during check-in:', err);
    } finally {
      setLoading(false);
    }
  }, [
    state.availableClasses,
    state.selectedStudent,
    state.selectedClasses,
    setLoading,
    setError,
    showThankYouMessage,
    hideThankYouMessage,
    resetForm,
  ]);

  // Computed values
  const isCheckInEnabled = state.selectedStudent && state.selectedClasses.length > 0;

  return {
    ...context,
    // Additional methods
    fetchStudentClasses,
    handleStudentSelect,
    handleCheckIn,
    // Computed values
    isCheckInEnabled,
  };
}